export const isValidCoordinate = (coord) => {
  if (!Array.isArray(coord) || coord.length !== 2) return false;
  const [lon, lat] = coord;
  if (typeof lon !== "number" || typeof lat !== "number") return false;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;
  if (lon < -180.0 || lon > 180.0) return false;
  if (lat < -85.0511 || lat > 85.0511) return false;
  return true;
};

const buildLegacyWaypoints = (mapCoords) => {
  if (!mapCoords) return [];
  const waypoints = [];
  if (isValidCoordinate(mapCoords.from)) {
    waypoints.push({
      id: "legacy-from",
      coordinate: mapCoords.from,
      title: "Start",
      containerIndex: 0,
    });
  }
  if (isValidCoordinate(mapCoords.to)) {
    waypoints.push({
      id: "legacy-to",
      coordinate: mapCoords.to,
      title: "Slutt",
      containerIndex: 1,
    });
  }
  return waypoints;
};

export const normalizeMapConfig = (sectionOrConfig) => {
  const existingConfig = sectionOrConfig?.mapConfig
    ? sectionOrConfig.mapConfig
    : (sectionOrConfig?.waypoints || sectionOrConfig?.theme || sectionOrConfig?.routeStyle)
      ? sectionOrConfig
      : {};
  const theme = existingConfig.theme || "osm";
  const routeStyle = existingConfig.routeStyle || "flight";
  const vignette = existingConfig.vignette ?? true;
  const compassRose = existingConfig.compassRose ?? (theme === "antique-parchment");
  const showHud = existingConfig.showHud ?? false;
  const distanceUnit = existingConfig.distanceUnit || "km";
  const historicOverlay = existingConfig.historicOverlay || undefined;

  let waypoints = [];
  if (Array.isArray(existingConfig.waypoints) && existingConfig.waypoints.length > 0) {
    waypoints = existingConfig.waypoints.map((wp, i) => ({
      id: wp.id || `wp-${i}`,
      coordinate: wp.coordinate,
      title: wp.title || `Stopp ${i + 1}`,
      zoom: wp.zoom,
      thumbnail: wp.thumbnail,
      containerIndex: wp.containerIndex ?? i,
      historicMapOpacity: wp.historicMapOpacity,
    }));
  } else {
    waypoints = buildLegacyWaypoints(sectionOrConfig?.mapCoords);
  }

  const from = waypoints[0]?.coordinate || null;
  const to = waypoints.length > 1 ? waypoints[waypoints.length - 1]?.coordinate : null;

  return {
    theme,
    routeStyle,
    vignette,
    compassRose,
    showHud,
    distanceUnit,
    waypoints,
    historicOverlay,
    from,
    to,
  };
};

export const buildSectionMapPatch = (mapConfig = {}) => {
  const waypoints = Array.isArray(mapConfig.waypoints) ? mapConfig.waypoints : [];
  const from = waypoints[0]?.coordinate || null;
  const to = waypoints.length > 1 ? waypoints[waypoints.length - 1]?.coordinate : null;

  return {
    mapConfig: {
      theme: mapConfig.theme || "osm",
      routeStyle: mapConfig.routeStyle || "flight",
      vignette: mapConfig.vignette ?? true,
      compassRose: mapConfig.compassRose ?? false,
      showHud: mapConfig.showHud ?? false,
      distanceUnit: mapConfig.distanceUnit || "km",
      waypoints: waypoints.map((wp) => ({
        ...wp,
        historicMapOpacity: wp.historicMapOpacity,
      })),
      ...(mapConfig.historicOverlay ? { historicOverlay: mapConfig.historicOverlay } : {}),
    },
    mapCoords: {
      from,
      to,
    },
  };
};

export const addWaypoint = (config, newWaypoint) => {
  const currentWaypoints = config?.waypoints || [];
  const id = newWaypoint.id || `wp-${Date.now()}-${currentWaypoints.length + 1}`;
  const waypoint = {
    id,
    coordinate: newWaypoint.coordinate,
    title: newWaypoint.title || `Stopp ${currentWaypoints.length + 1}`,
    zoom: newWaypoint.zoom,
    thumbnail: newWaypoint.thumbnail,
    containerIndex: newWaypoint.containerIndex ?? currentWaypoints.length,
    historicMapOpacity: newWaypoint.historicMapOpacity,
  };

  const waypoints = [...currentWaypoints, waypoint];
  return {
    ...config,
    waypoints,
  };
};

export const updateWaypoint = (config, index, patch) => {
  const currentWaypoints = config?.waypoints || [];
  if (index < 0 || index >= currentWaypoints.length) {
    return config;
  }

  const waypoints = currentWaypoints.map((wp, i) => {
    if (i !== index) return wp;
    return { ...wp, ...patch };
  });

  return {
    ...config,
    waypoints,
  };
};

export const removeWaypoint = (config, index) => {
  const currentWaypoints = config?.waypoints || [];
  if (index < 0 || index >= currentWaypoints.length) {
    return config;
  }

  const waypoints = currentWaypoints.filter((_, i) => i !== index);
  return {
    ...config,
    waypoints,
  };
};

export const reorderWaypoints = (config, fromIndex, toIndex) => {
  const currentWaypoints = config?.waypoints || [];
  if (
    fromIndex < 0 ||
    fromIndex >= currentWaypoints.length ||
    toIndex < 0 ||
    toIndex >= currentWaypoints.length ||
    fromIndex === toIndex
  ) {
    return config;
  }

  const copy = [...currentWaypoints];
  const [removed] = copy.splice(fromIndex, 1);
  copy.splice(toIndex, 0, removed);

  return {
    ...config,
    waypoints: copy,
  };
};

export const computeMapSectionMultiplier = (containersCount) => {
  if (containersCount == null) return 3;
  return Math.max(3, containersCount * 1.5);
};

const getWaypointOpacity = (wp, idx, total) => {
  if (wp?.historicMapOpacity !== undefined && wp?.historicMapOpacity !== null) {
    return Number(wp.historicMapOpacity);
  }
  return idx === 0 ? (total <= 1 ? 0.85 : 1.0) : 0.0;
};

export const computeHistoricLayerOpacity = ({
  waypoints = [],
  activeStepIndex,
  progress,
}) => {
  if (!Array.isArray(waypoints) || waypoints.length === 0) return 0.0;
  const n = waypoints.length;

  if (typeof progress === "number") {
    if (n <= 1) {
      const op = getWaypointOpacity(waypoints[0], 0, n);
      return Math.max(0.0, Math.min(1.0, op));
    }
    const scaled = Math.max(0.0, Math.min(1.0, progress)) * (n - 1);
    const idx = Math.min(Math.floor(scaled), n - 2);
    const ratio = scaled - idx;
    const op0 = getWaypointOpacity(waypoints[idx], idx, n);
    const op1 = getWaypointOpacity(waypoints[idx + 1], idx + 1, n);
    const interpolated = op0 + (op1 - op0) * ratio;
    return Math.max(0.0, Math.min(1.0, interpolated));
  }

  if (typeof activeStepIndex === "number") {
    const op = getWaypointOpacity(waypoints[activeStepIndex], activeStepIndex, n);
    return Math.max(0.0, Math.min(1.0, op));
  }

  return 0.0;
};

export const isSafeHistoricTileUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed.startsWith("https://")) return false;
  if (/javascript:|data:|vbscript:/i.test(trimmed)) return false;
  try {
    const testUrl = trimmed.replace(/\{[^}]+\}/g, "0");
    const parsed = new URL(testUrl);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
};
